"use client";

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
const slides = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5410.jpg",
  eyebrow: "01 // Canopy Care",
  title: "Precision Canopy Management",
  description: "Advanced arboricultural techniques for optimal health and structural integrity of your Fort Worth property's canopy."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5151.jpg",
  eyebrow: "02 // Risk Management",
  title: "Hazardous Tree Removal",
  description: "Clinical precision in dismantling compromised or hazardous trees in confined residential spaces."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5543.jpg",
  eyebrow: "03 // Emergency Care",
  title: "24/7 Emergency Response",
  description: "Rapid deployment teams ready 24/7 to secure your property after severe DFW weather events."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/_app_sd_ATOM_001_DCIM_PTSC_0017.jpg",
  eyebrow: "04 // Professional Assessment",
  title: "Expert Arborist Diagnostics",
  description: "Comprehensive evaluation and targeted treatments to preserve the legacy trees of Arbor Pro clients."
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/_app_sd_ATOM_001_DCIM_PTSC_0012.jpg",
  eyebrow: "05 // Site Restoration",
  title: "Complete Site Clearance",
  description: "Complete sub-surface eradication of remnants, preparing your landscape for its next phase."
}];
export default function HeroSlideshow() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const onSelect = useCallback((api: CarouselApi) => {
    setCurrent(api.selectedScrollSnap());
  }, []);
  useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api, onSelect]);
  useEffect(() => {
    if (!api) return;
    const intervalId = setInterval(() => {
      api.scrollNext();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [api]);
  return <section id="heroslideshow" className="relative w-full h-[85vh] min-h-[600px] bg-primary overflow-hidden group">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-5 z-0 pointer-events-none" />

      <Carousel setApi={setApi} opts={{
      loop: true,
      align: "start"
    }} className="w-full h-full">
        <CarouselContent className="h-full ml-0">
          {slides.map((slide, i) => <CarouselItem key={i} data-index={i} className="relative w-full h-full pl-0">
              {}
              <div className="absolute inset-0 z-0">
                <img src={slide.src} data-wvc-srcset={`${slide.src}-wvc-srcset`} data-wvc-sizes={`${slide.src}-wvc-sizes`} alt={slide.title} className="w-full h-full object-cover" loading={i === 0 ? "eager" : "lazy"} />
                {}
                <div className="absolute inset-0 bg-primary/50 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/40 to-primary/10" />
                <div className="absolute inset-0 bg-gradient-to-r from-primary/80 via-transparent to-transparent" />
              </div>

              {}
              <div className="relative z-10 flex flex-col justify-center h-full container mx-auto px-6 md:px-12 lg:px-24">
                <div className="max-w-3xl">
                  {slide.eyebrow && <div className="flex items-center gap-4 mb-6 overflow-hidden">
                      <div className="h-px w-8 bg-accent" />
                      <span className="font-mono text-accent text-xs md:text-sm tracking-[0.2em] uppercase font-semibold">
                        {slide.eyebrow}
                      </span>
                    </div>}
                  
                  {slide.title && <h1 className="font-default text-5xl md:text-6xl lg:text-8xl font-bold text-primary-foreground leading-[1.1] mb-6 uppercase tracking-tight">
                      {slide.title}
                    </h1>}
                  
                  {slide.description && <p className="font-sans text-lg md:text-xl text-primary-foreground/80 mb-10 max-w-2xl font-light border-l-2 border-accent/50 pl-6">
                      {slide.description}
                    </p>}
                  
                  <div className="flex flex-wrap gap-4">
                    <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-mono uppercase tracking-wider h-14 px-8">
                      <Link to="/GetQuotePage">Request Quote</Link>
                    </Button>
                    <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground hover:text-primary font-mono uppercase tracking-wider h-14 px-8 bg-transparent backdrop-blur-sm">
                      <Link to="/OurExpertisePage">Our Services</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </CarouselItem>)}
        </CarouselContent>

        {}
        <div className="absolute bottom-8 left-0 right-0 z-20 container mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          
          {}
          <div className="flex items-center gap-3">
            <span className="font-mono text-primary-foreground/50 text-sm w-8">
              {current + 1 < 10 ? `0${current + 1}` : current + 1}
            </span>
            <div className="flex gap-2">
              {slides.map((_, i) => <Button key={i} data-index={i} variant="ghost" size="sm" onClick={() => api?.scrollTo(i)} className={`h-1 px-0 py-0 transition-all duration-500 ease-in-out ${current === i ? "w-12 bg-accent hover:bg-accent" : "w-4 bg-primary-foreground/20 hover:bg-primary-foreground/40"}`} aria-label={`Go to slide ${i + 1}`} />)}
            </div>
            <span className="font-mono text-primary-foreground/50 text-sm w-8 text-right">
              {slides.length < 10 ? `0${slides.length}` : slides.length}
            </span>
          </div>

          {}
          <div className="flex gap-2">
            <CarouselPrevious className="static translate-y-0 translate-x-0 size-12 border-primary-foreground/20 text-primary-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent bg-transparent backdrop-blur-sm transition-colors" />
            <CarouselNext className="static translate-y-0 translate-x-0 size-12 border-primary-foreground/20 text-primary-foreground hover:bg-accent hover:text-accent-foreground hover:border-accent bg-transparent backdrop-blur-sm transition-colors" />
          </div>
        </div>
      </Carousel>

      {}
      <div className="absolute top-8 left-8 size-8 border-t-2 border-l-2 border-accent/40 z-20 pointer-events-none hidden lg:block" />
      <div className="absolute top-8 right-8 size-8 border-t-2 border-r-2 border-accent/40 z-20 pointer-events-none hidden lg:block" />
      <div className="absolute bottom-32 left-8 size-8 border-b-2 border-l-2 border-accent/40 z-20 pointer-events-none hidden lg:block" />
      <div className="absolute bottom-32 right-8 size-8 border-b-2 border-r-2 border-accent/40 z-20 pointer-events-none hidden lg:block" />
      
      {}
      <div className="absolute top-1/2 left-8 -translate-y-1/2 w-6 h-px bg-accent/30 z-20 pointer-events-none hidden lg:block" />
      <div className="absolute top-1/2 left-11 -translate-y-1/2 w-px h-6 -translate-x-1/2 bg-accent/30 z-20 pointer-events-none hidden lg:block" />
      
      <div className="absolute top-1/2 right-8 -translate-y-1/2 w-6 h-px bg-accent/30 z-20 pointer-events-none hidden lg:block" />
      <div className="absolute top-1/2 right-11 -translate-y-1/2 w-px h-6 translate-x-1/2 bg-accent/30 z-20 pointer-events-none hidden lg:block" />
    </section>;
}